import React, { useEffect } from 'react';

import { Box, Flex, Image, Text, TextProps } from '@opendoor/bricks/core';
import { centsToPrettyCurrencyString } from '@opendoor/shared-fe/build/formatUtils';
import { PreClosingOffer } from 'sellDirect/OfferContext';
import { useObservability } from 'shared/helpers/observability';

const OPENDOOR_LOGO_BLUE = 'https://imgdrop.imgix.net/5156988f21ce40a8ac81b1cf0a3c4f59.png';

interface AvatarsProps {
  size: 'sm' | 'md';
}

/** Avatars */
export const Avatars: React.FC<AvatarsProps> = ({ size }) => {
  const iconSize = size === 'sm' ? '24px' : '44px';
  const fontSize = size === 'sm' ? '8px' : '13px';

  return (
    // avatars are purely decorative
    <Flex aria-hidden>
      <Box width={iconSize} height={iconSize} borderRadius="rounded" overflow="hidden">
        <Image height="auto" src={OPENDOOR_LOGO_BLUE} alt={'alt'} objectFit="cover" />
      </Box>
      <Flex
        width={iconSize}
        height={iconSize}
        borderRadius="rounded"
        overflow="hidden"
        bgColor="neutrals100"
        position="relative"
        left="-8px"
        alignItems="center"
        justifyContent="center"
      >
        {/* @ts-expect-error: 8px and 13px are not a valid Bricks size, but is intended as a one-off custom size. */}
        <Text color="neutrals0" fontWeight="bold" fontSize={fontSize} lineHeight="s000">
          MLS
        </Text>
      </Flex>
    </Flex>
  );
};

interface IOfferCardHeadlinePrice {
  offer: PreClosingOffer;
  /** Whether to show the headline range with a blue gradient behind it */
  withGradient?: boolean;
}

/**
 * Renders the listing range for the LwC offer card
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const OfferCardHeadlineRange: React.FC<IOfferCardHeadlinePrice & TextProps> = ({
  offer,
  withGradient = true,
  ...textProps
}) => {
  const { trackError } = useObservability();

  const { listingEstimateFloorCents: minCents, listingEstimateCeilingCents: maxCents } = offer;

  const hasRange = !!minCents && !!maxCents;

  useEffect(() => {
    if (!hasRange) {
      trackError('cake-lwc-headline-missing-valuation-range', undefined, {
        offerId: offer.id,
        listingEstimateFloorCents: offer.listingEstimateFloorCents,
        listingEstimateCeilingCents: offer.listingEstimateCeilingCents,
      });
    }
  }, [
    trackError,
    hasRange,
    offer.id,
    offer.listingEstimateFloorCents,
    offer.listingEstimateCeilingCents,
  ]);

  return (
    <Text
      // @ts-expect-error: 50px is not a normal bricks size, but this is a one-off
      fontSize={['s4', null, '50px', 's6']}
      color="neutrals100"
      fontWeight="bold"
      lineHeight={['s3', null, 's6']}
      bgGradient={
        withGradient ? '-webkit-linear-gradient(180deg, #1C85E8 48.24%, #014381 87.25%)' : undefined
      }
      bgClip={withGradient ? 'text' : undefined}
      filter={hasRange ? 'none' : 'blur(15px)'}
      aria-label={
        hasRange
          ? `Approximately from ${centsToPrettyCurrencyString(
              minCents,
            )} to ${centsToPrettyCurrencyString(maxCents)}`
          : 'We’re working on generating an offer range'
      }
      {...textProps}
    >
      {/* aria-hidden because screen readers will use the more friendly aria-label instead */}
      <Box as="span" aria-hidden>
        {centsToPrettyCurrencyString(minCents)} - {centsToPrettyCurrencyString(maxCents)}
      </Box>
    </Text>
  );
};
